import { iconStroke } from '../lib/icons.js';

/**
 * De tekentjes die op meer dan één plek staan.
 *
 * Regel uit CLAUDE.md: komt hetzelfde tekentje een tweede keer voor, dan wordt het
 * een component. Zo blijven twee kruisjes niet uit elkaar lopen, en dat gebeurde:
 * eenendertig kruisjes met vijf maten en zeven lijndiktes.
 *
 * Elk tekentje staat op een viewBox van 24 en erft zijn kleur van de knop
 * eromheen (`currentColor`). De lijndikte volgt uit de maat, zodat hij op het scherm
 * altijd 2,2 pixels is; zie `iconStroke` in lib/icons.js.
 *
 * Staat een tekentje nog maar op één plek, laat hem dan gewoon in de JSX staan. Een
 * component voor één plek is een omweg.
 */
function Svg({ maat, kinderen, fill = 'none' }) {
  return (
    <svg
      width={maat}
      height={maat}
      viewBox="0 0 24 24"
      fill={fill}
      stroke="currentColor"
      strokeWidth={iconStroke(maat)}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {kinderen}
    </svg>
  );
}

/** Sluiten. Twintig is de maat die zestien van de eenendertig al hadden. */
export function Kruis({ maat = 20 }) {
  return <Svg maat={maat} kinderen={<><path d="M18 6 6 18" /><path d="m6 6 12 12" /></>} />;
}

/** Een slot: de pincode. */
export function Slot({ maat = 20 }) {
  return <Svg maat={maat} kinderen={<><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>} />;
}

/** Een gezichtje: de tekentjeslade. */
export function Gezicht({ maat = 20 }) {
  return (
    <Svg maat={maat} kinderen={<>
      <circle cx="12" cy="12" r="9.2" />
      <path d="M8.5 14.5a4.5 4.5 0 0 0 7 0" />
      <path d="M9 9.5h.01" />
      <path d="M15 9.5h.01" />
    </>} />
  );
}

/**
 * Een hartje. `gevuld` bepaalt of het vol staat: dat is het enige verschil tussen
 * een hartje dat je gaf en een hartje dat je kunt geven.
 */
export function Hartje({ maat = 20, gevuld = false }) {
  return (
    <Svg maat={maat} fill={gevuld ? 'var(--roze)' : '#fff'}
      kinderen={<path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8Z" />} />
  );
}

/** Een potlood: vrij tekenen. Zelfde tekening als in de gereedschapskolom. */
export function Potlood({ maat = 20 }) {
  return <Svg maat={maat} kinderen={<><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" /></>} />;
}

/**
 * Een kader: een vlak aanwijzen.
 *
 * Letterlijk dezelfde rechthoek als het kader in de gereedschapskolom
 * (`showMarqueeIcon` in views/screens/Studio.jsx). Hij was hier gestippeld en een
 * maatje anders, en dan is het hetzelfde gereedschap met twee gezichten.
 */
export function Kader({ maat = 20 }) {
  return <Svg maat={maat} kinderen={<rect x="3.8" y="3.8" width="16.4" height="16.4" rx="1.2" />} />;
}

/**
 * Een pijl in de rondte: opnieuw beginnen.
 *
 * De boog loopt 315 graden, van rechts kloksgewijs, en laat dus vijfenveertig graden
 * open in de rechterbovenhoek: bijna zeven pixels gat op een viewBox van 24.
 *
 * Dat is dezelfde opening als de eerste versie had, en die zag eruit als een dichte
 * cirkel. Niet omdat het gat te klein was, maar omdat het pijlpuntje er dwars overheen
 * lag. Nu staat het puntje aan het eind van de boog en wijst het mee met de draai, en
 * dan zie je die vijfenveertig graden wel.
 *
 * Hij liep eerst 315 graden en het pijlpuntje lag precies over die smalle opening heen;
 * dan is het een dichte cirkel met een streepje eraan, wat je ook met dat streepje
 * doet. Een ronddraaiende pijl moet je aan de onderbreking kunnen zien.
 *
 * De getallen zijn uitgerekend en niet geschat: hart (12,12), straal 8,5, en het
 * puntje twee armen van drie lang, veertig graden terug vanaf de rijrichting.
 *
 * Dit is een tekentje en geen woord, en dat is een gedwongen keuze: in een raster van
 * zes is een vakje eenenvijftig pixels breed en het woord "Opnieuw" heeft er
 * negenenvijftig nodig. Het lettertype verkleinen om een layout te laten passen is de
 * verkeerde kant op, dus staat het woord in de tooltip.
 */
export function Herstel({ maat = 20 }) {
  return (
    <Svg maat={maat} kinderen={<>
      <path d="M20.5 12a8.5 8.5 0 1 1 -2.49 -6.01" />
      <path d="M17.75 3 18.01 5.99 15.02 5.73" />
    </>} />
  );
}

/** Een chatbubbel. */
export function Bubbel({ maat = 20 }) {
  return <Svg maat={maat} kinderen={<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />} />;
}

/**
 * Een luidspreker, met of zonder golfjes. De doorgestreepte stand heeft een
 * kruisje in plaats van golfjes: een streep dwars over het hele icoontje leest op
 * twintig pixels als vuil op je scherm.
 */
export function Geluid({ maat = 20, stil = false }) {
  return (
    <Svg maat={maat} kinderen={<>
      <path d="M4 9.5h3.2L12 5.5v13l-4.8-4H4z" />
      {stil
        ? <><path d="M16.5 9.5 21 14" /><path d="M21 9.5 16.5 14" /></>
        : <><path d="M16.2 9a4.4 4.4 0 0 1 0 6" /><path d="M19 6.6a8 8 0 0 1 0 10.8" /></>}
    </>} />
  );
}

/**
 * De vier tekentjes van de programma's die vensters zijn geworden.
 *
 * Ze staan in de taakbalk naast het potlood, en daar geldt de regel van sectie 8:
 * op twintig pixels blijft alleen een doorlopend silhouet overeind. Vandaar geen
 * losse streepjes en geen binnenwerk dat dichtloopt.
 */

/** Een joystick: de arcadehal. Een bal op een stok, op een voetje. */
export function Joystick({ maat = 20 }) {
  return (
    <Svg maat={maat} kinderen={<>
      <circle cx="12" cy="5.6" r="3" />
      <path d="M12 8.6v6.4" />
      <path d="M6.4 20.4l1.8-5.4h7.6l1.8 5.4z" />
    </>} />
  );
}

/** Een beker: het leaderbord. Kom met twee oren, op een voet. */
export function Beker({ maat = 20 }) {
  return (
    <Svg maat={maat} kinderen={<>
      <path d="M7 3.6h10v4.8a5 5 0 0 1-10 0z" />
      <path d="M7 5.2H4.4a3 3 0 0 0 2.9 3" />
      <path d="M17 5.2h2.6a3 3 0 0 1-2.9 3" />
      <path d="M12 13.4v3.4" />
      <path d="M8 20.4h8" />
      <path d="M9.6 20.4l.6-3.6h3.6l.6 3.6" />
    </>} />
  );
}

/** Een boek: het gastenboek. Eén omslag met een rug in het midden. */
export function Boek({ maat = 20 }) {
  return (
    <Svg maat={maat} kinderen={<>
      <path d="M12 6.4C10.4 5 8.4 4.4 4.6 4.4v13.4c3.8 0 5.8.6 7.4 2 1.6-1.4 3.6-2 7.4-2V4.4c-3.8 0-5.8.6-7.4 2z" />
      <path d="M12 6.4v13.4" />
    </>} />
  );
}

/** Een sterretje: de krabbel van de dag. Eén doorlopende vorm. */
export function Ster({ maat = 20 }) {
  return (
    <Svg maat={maat} kinderen={<path d="M12 3.4l2.6 5.6 6 .8-4.4 4.3 1.1 6.1L12 17.3l-5.3 3 1.1-6.2L3.4 9.8l6-.8z" />} />
  );
}
